// src/pages/produtos/produtos/ProdutoImagemModal.js
import React, { useEffect, useRef, useState } from 'react';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { obterArquivoImagemProduto } from '../../../services/produtos/ImagemService';
import { CATALOG_IMAGE_ACCEPT, processCatalogImageFile } from '../../../utils/ImageUtils.js';

const MAX_IMAGE_HEIGHT = 400;

const getImageMimeType = (fileName = '') => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    if (extension === 'png') return 'image/png';
    if (extension === 'webp') return 'image/webp';
    if (extension === 'gif') return 'image/gif';

    return 'image/jpeg';
};

const ProdutoImagemModal = ({
    show,
    onHide,
    produto,
    setProduto,
}) => {
    const { showMessage } = useMessage();
    const [imagemPreview, setImagemPreview] = useState(null);
    const [imagemSel, setImagemSel] = useState(null);
    const [carregandoImagem, setCarregandoImagem] = useState(false);
    const objectUrlRef = useRef(null);
    const carregamentoRef = useRef(0);

    const revogarObjectUrl = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    };

    const definirPreviewBlob = (blob) => {
        revogarObjectUrl();
        objectUrlRef.current = URL.createObjectURL(blob);
        setImagemPreview(objectUrlRef.current);
    };

    useEffect(() => () => {
        carregamentoRef.current += 1;
        revogarObjectUrl();
    }, []);

    useEffect(() => {
        if (!show) {
            carregamentoRef.current += 1;
            revogarObjectUrl();
            return;
        }

        carregamentoRef.current += 1;
        setCarregandoImagem(false);

        if (produto?.imagem?.arquivo) {
            revogarObjectUrl();
            setImagemSel(produto.imagem);
            setImagemPreview(`data:${getImageMimeType(produto.imagem.nome)};base64,${produto.imagem.arquivo}`);
            return;
        }

        if (produto?.imagem?.nome) {
            setImagemSel(produto.imagem);
            setImagemPreview(null);
            return;
        }

        setImagemSel(null);
        setImagemPreview(null);
    }, [show, produto?.id, produto?.imagem, produto?.imagemId]);

    const handleImagemChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        carregamentoRef.current += 1;
        setCarregandoImagem(false);

        try {
            const imagemProcessada = await processCatalogImageFile(file, { maxHeight: MAX_IMAGE_HEIGHT });
            const imagemPayload = {
                id: produto.imagemId ? produto.imagemId : 0,
                nome: imagemProcessada.nome,
                tamanho: imagemProcessada.tamanho,
                arquivo: imagemProcessada.arquivo,
            };

            revogarObjectUrl();
            setImagemSel(imagemPayload);
            setImagemPreview(imagemProcessada.previewUrl);
            setProduto({ ...produto, imagem: imagemPayload });
        } catch (error) {
            showMessage(error.message || 'Erro ao processar a imagem.', 'warning');
            e.target.value = '';
        }
    };

    const handleCarregaImagem = async (e) => {
        e.preventDefault();

        const carregamentoAtual = ++carregamentoRef.current;
        try {
            setCarregandoImagem(true);
            const result = await obterArquivoImagemProduto(produto.id);
            if (carregamentoAtual !== carregamentoRef.current) return;
            if (!result) {
                showMessage('Imagem do produto não encontrada.', 'warning');
                return;
            }

            definirPreviewBlob(result.blob);
            setImagemSel({
                id: produto.imagemId,
                nome: result.nome || produto.imagem?.nome || 'Imagem cadastrada',
                tamanho: result.blob.size,
                arquivo: null,
            });
        } catch (error) {
            if (carregamentoAtual === carregamentoRef.current) {
                showMessage(error.message || 'Erro ao carregar imagem do produto.', 'warning');
            }
        } finally {
            if (carregamentoAtual === carregamentoRef.current) {
                setCarregandoImagem(false);
            }
        }
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura="600px" zIndex={1070}>
            <ModalHeader>
                <h5 className="modal-title">Imagem do Produto</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
            </ModalHeader>
            <ModalBody>
                <div className="form-group">
                    <label>Imagem:</label>
                    <div className="d-flex align-items-center">
                        {(produto.imagemId && !imagemPreview) && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleCarregaImagem}
                                disabled={carregandoImagem}
                            >
                                {carregandoImagem ? 'Carregando...' : 'Carregar Imagem'}
                            </button>
                        )}
                        <input
                            type="file"
                            accept={CATALOG_IMAGE_ACCEPT}
                            onChange={handleImagemChange}
                            disabled={carregandoImagem || (produto.imagemId && !imagemPreview)}
                        />
                        <input
                            type="text"
                            className="form-control ml-2"
                            readOnly
                            value={imagemSel?.nome ? imagemSel.nome : ''}
                            disabled
                        />
                    </div>
                </div>
                {imagemPreview && (
                    <div className="mt-3">
                        <img src={imagemPreview} alt="Pre-visualizacao" style={{ maxHeight: '250px', maxWidth: '100%' }} />
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                    Fechar
                </button>
            </ModalFooter>
        </ModalContainer>
    );
};

export default ProdutoImagemModal;
